import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { History, Clock, Loader2, Search, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HistoryView = ({ currentIcao, getFlightCategoryStyle }) => {
  const [icao, setIcao] = useState(currentIcao || "");
  const [hours, setHours] = useState("24");
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    const code = icao.toUpperCase().trim();
    if (code.length !== 4) {
      toast.error("Please enter a valid 4-character ICAO code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/history/${code}`, {
        params: { hours: parseInt(hours) }
      });
      setHistoryData(response.data);
    } catch (error) {
      toast.error("Failed to fetch historical data");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryStats = () => {
    if (!historyData?.history) return null;

    const stats = { VFR: 0, MVFR: 0, IFR: 0, LIFR: 0 };
    historyData.history.forEach(item => {
      if (stats.hasOwnProperty(item.flight_category)) {
        stats[item.flight_category]++;
      }
    });

    const total = historyData.history.length;
    return Object.entries(stats).map(([category, count]) => ({
      category,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    }));
  };

  const parseMetarTime = (raw) => {
    // Extract time from METAR (format: DDHHMMZ)
    const match = raw.match(/\b(\d{6})Z\b/);
    if (match) {
      const time = match[1];
      const day = time.substring(0, 2);
      const hour = time.substring(2, 4);
      const minute = time.substring(4, 6);
      return `Day ${day}, ${hour}:${minute}Z`;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Search Controls */}
      <div className="weather-card p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <History className="w-5 h-5 text-blue-600" />
          Historical Weather Data
        </h3>

        <div className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Airport ICAO
            </label>
            <Input
              type="text"
              placeholder="e.g., KJFK"
              value={icao}
              onChange={(e) => setIcao(e.target.value.toUpperCase())}
              className="search-input uppercase"
              maxLength={4}
              data-testid="history-icao-input"
            />
          </div>

          <div className="w-full sm:w-48">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">
              Time Period
            </label>
            <Select value={hours} onValueChange={setHours}>
              <SelectTrigger className="bg-white border-slate-300 h-12" data-testid="history-hours-select">
                <SelectValue placeholder="Select hours" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="6">Last 6 hours</SelectItem>
                <SelectItem value="12">Last 12 hours</SelectItem>
                <SelectItem value="24">Last 24 hours</SelectItem>
                <SelectItem value="48">Last 48 hours</SelectItem>
                <SelectItem value="72">Last 72 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={fetchHistory}
            disabled={loading || icao.length !== 4}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 h-12 w-full sm:w-auto shadow-md"
            data-testid="fetch-history-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Fetch History
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Results */}
      {historyData && (
        <>
          {/* Stats Overview */}
          <div className="weather-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Flight Category Distribution
              </h3>
              <Badge variant="outline" className="border-slate-300 text-slate-600">
                {historyData.history?.length || 0} observations
              </Badge>
            </div>

            {getCategoryStats() && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {getCategoryStats().map(({ category, count, percentage }) => (
                  <div
                    key={category}
                    className={`p-4 rounded-lg border ${getFlightCategoryStyle(category)}`}
                  >
                    <p className="text-2xl font-bold">{percentage}%</p>
                    <p className="text-sm opacity-80">{category}</p>
                    <p className="text-xs opacity-60">{count} obs</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Historical METARs */}
          <div className="weather-card p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Historical METARs - {historyData.icao}
            </h3>

            <ScrollArea className="h-96" data-testid="history-list">
              <div className="space-y-3">
                {historyData.history?.map((item, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-lg bg-slate-50 border border-slate-200 hover:border-slate-300 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Badge className={`${getFlightCategoryStyle(item.flight_category)} border text-xs`}>
                        {item.flight_category}
                      </Badge>
                      {parseMetarTime(item.raw) && (
                        <span className="text-xs text-slate-500 font-mono">
                          {parseMetarTime(item.raw)}
                        </span>
                      )}
                    </div>
                    <p className="font-mono text-sm text-slate-600 break-all">
                      {item.raw}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </>
      )}

      {/* Empty State */}
      {!historyData && !loading && (
        <div className="text-center py-16">
          <div className="inline-flex p-4 rounded-full bg-slate-100 mb-6">
            <History className="w-12 h-12 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-700 mb-2">
            View Historical Weather
          </h2>
          <p className="text-slate-500 max-w-md mx-auto">
            Enter an ICAO code and select a time period to view historical METAR observations and flight category trends.
          </p>
        </div>
      )}
    </div>
  );
};

export default HistoryView;
